/**
 * The message body as HTML (#1530): what the composer sends, what the bubble
 * shows, what an edit loads. THIN BY DESIGN, and every rule in it is the
 * house's: the length is `convertToPlainText`'s, legacy detection is
 * `isLegacyPlainTextAreaValue`'s, the allow-list is `sanitizeTextArea`'s
 * (with the one `imgSchemes` option #1530 added), and editor previews go
 * through the same `pendingInlineImagePreview` map a request field uses. If
 * something here starts re-implementing one of those, stop and import it.
 *
 * THE TWO SCHEMES. An image in a message is a message FILE (C3), named from
 * the HTML by one of two schemes:
 *   `correspondence-inline://<n>`   a file in THIS call's `files`, by index;
 *                                   the client cannot know its id yet.
 *   `correspondence-file://<id>`    a file the message HOLDS; what the server
 *                                   stores and sends back, and what an edit
 *                                   sends back untouched.
 * While a picture is only STAGED the editor refers to it as
 * `correspondence-inline://<stagedId>` (`inlineImageReference`); the rewrite
 * at send (`outgoingMessage`) turns the staged id into the index. A ref the
 * rewrite misses is non-numeric and the server drops that `<img>`: the safe
 * failure, never a wrong file.
 */
import { Platform } from 'react-native';

import { partitionForDisplay } from './attachmentPieces';
import {
  MAX_MESSAGE_HTML_LENGTH,
  MAX_MESSAGE_LENGTH,
} from './providers/provider.types';
import {
  INLINE_IMAGE_SCHEME,
  inlineImageReference,
} from '../../services/correspondence/stagedFile';
import {
  isDisplayableInlineImageSrc,
  pendingInlineImagePreviewSrc,
  rememberPendingInlineImagePreview,
} from '../../services/pendingInlineImagePreview';
import { convertToPlainText } from '../../utils/richContent/convertToPlainText';
import { imagePlaceholderForTag } from '../../utils/richContent/richHtmlAttachmentRefs';
import {
  isLegacyPlainTextAreaValue,
  sanitizeTextArea,
} from '../../utils/richContent/sanitizeTextArea';
import { escapeHtml } from '../richHtmlContentShared';
import { CONFIG_TEAM_LABEL } from './correspondence.types';

import type { MessageFile, MessageMention } from './correspondence.types';
import type { OutgoingMessageFile } from './providers/provider.types';
import type { StagedFile } from '../../services/correspondence/stagedFile';

/** The scheme of an image the message holds; the server's `FILE_IMAGE_SCHEME`. */
export const FILE_IMAGE_SCHEME = 'correspondence-file';

/**
 * The house tags and style properties the server's message rule DROPS
 * (#1530 Teams parity, owner 2026-10-05): the client's mirror of
 * `correspondenceSanitizer.ts`'s `MESSAGE_DROPPED_TAGS` / `_STYLES`, pinned
 * equal in `messageHtml.test.ts` the way the byte limits are. Applied on the
 * preview AND on the send path, so a pasted centred Word paragraph is shown
 * flat and leaves the device flat, exactly as the server would store it;
 * the client never previews what the server will not keep.
 */
export const MESSAGE_DROPPED_TAGS: readonly string[] = ['sub', 'sup'];
export const MESSAGE_DROPPED_STYLES: readonly string[] = [
  'text-align',
  'font-family',
];

/**
 * The attributes the message rule admits on a `span` (#1599): the client's
 * mirror of `correspondenceSanitizer.ts`'s `MENTION_SPAN_ATTRIBUTES`,
 * duplicated under the same house workaround and pinned equal in
 * `messageHtml.test.ts`. The client keeps ANY value: the server is
 * authoritative on the id's shape and on `FEATURE_CORRESPONDENCE_MENTIONS`,
 * and Story 1.4 reads the flag for the UI.
 */
export const MENTION_SPAN_ATTRIBUTES: readonly string[] = [
  'data-mention-user-id',
  'data-mention-group',
];

/**
 * The server's mention-id shape (#1599 Story 1.4): now defined in the pure
 * `mentionUserId.ts` so the 10tap WebView bundle can import it without
 * `react-native` (#1599 Story 3.1), and re-exported here unchanged.
 */
export {
  MENTION_USER_ID,
  MENTION_USER_ID_MAX,
  isMentionUserId,
} from './mentionUserId';

/**
 * A person token as the house sanitizer and the editor serialise it: a `span`
 * carrying `data-mention-user-id`, whose content is TEXT ONLY. A token whose
 * content carries markup (only a hand-crafted body could) does not match and
 * is left exactly as stored, which is the safe direction: its stored text
 * shows. The group token (`data-mention-group`) never matches this one; it
 * has its own, `GROUP_TOKEN`. Capture groups: 1 the attributes before the
 * id, 2 the quote, 3 the id, 4 the attributes after it, 5 the text.
 */
const MENTION_TOKEN =
  /<span\b([^>]*?)\sdata-mention-user-id=(["'])(\d+)\2([^>]*)>([^<]*)<\/span>/gi;

/**
 * The one collective token (#1599 Story 1.7): a `span` whose
 * `data-mention-group` is EXACTLY the literal `config-team`
 * (`CONFIG_TEAM_GROUP`, inline here to match `MENTION_TOKEN`'s shape), text
 * only. Any other value cannot be stored (the server's sanitizer keeps only
 * the literal) and is left alone here, defence in depth. NO `i` FLAG: an
 * attribute VALUE is case-sensitive, so `CONFIG-TEAM` is not the literal
 * (the sanitizer and the editor emit lowercase tag and attribute names, so
 * nothing real is missed). Capture groups: 1
 * the attributes before, 2 the quote, 3 the attributes after, 4 the text.
 */
const GROUP_TOKEN =
  /<span\b([^>]*?)\sdata-mention-group=(["'])config-team\2([^>]*)>([^<]*)<\/span>/g;

/** The few entities a token's text can carry, decoded for a label. */
const decodeTokenText = (text: string): string =>
  // `&amp;` last, after every other entity, so `&amp;lt;` decodes once to
  // `&lt;` and never twice to `<` (review 2026-10-22).
  decodeRemainingEntities(
    text
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>'),
  ).replace(/&amp;/g, '&');

/**
 * Each token's name as the DRAFT carries it, without the `@`, in document
 * order, once per id: the label of "Remove @Name and send" (#1599 Story 1.4,
 * AC 9). Built from the draft and never from the server's reply (NFR-3: the
 * refusal body carries ids, not names).
 *
 * PERSON TOKENS ONLY (#1599 Story 1.7, correction 10): a `group: true`
 * refusal is reachable only by an oversight writer on an unresolved
 * projection, and shows the "Not sent" line with no name for the team token
 * (deferred-work, 2026-10-22).
 */
export const mentionTokenNames = (
  html: string,
  userIds: readonly string[],
): string[] => {
  const wanted = new Set(userIds);
  const seen = new Set<string>();
  const names: string[] = [];
  for (const match of html.matchAll(MENTION_TOKEN)) {
    const id = match[3];
    if (!wanted.has(id) || seen.has(id)) continue;
    seen.add(id);
    names.push(decodeTokenText(match[5]).replace(/^@/, ''));
  }
  return names;
};

/**
 * The draft with every token naming one of `userIds` removed, text and all
 * (#1599 Story 1.4, AC 9: "Remove @Name and send"). The space the editor
 * inserted after the token goes with it, so `a @X b` becomes `a b`, not
 * `a  b`; a token with no following space takes the space before it when it
 * ends its paragraph. A token naming anyone else is untouched.
 *
 * PERSON TOKENS ONLY (#1599 Story 1.7, correction 10): the group token is
 * never stripped; a `group: true` refusal offers no "Remove and send"
 * (deferred-work, 2026-10-22).
 */
export const stripMentionTokens = (
  html: string,
  userIds: readonly string[],
): string => {
  const refused = new Set(userIds);
  if (refused.size === 0) return html;
  let out = '';
  let last = 0;
  for (const match of html.matchAll(MENTION_TOKEN)) {
    if (!refused.has(match[3]) || match.index === undefined) continue;
    let from = match.index;
    let to = from + match[0].length;
    const rest = html.slice(to);
    const following = /^(?: |&nbsp;|\u00a0)/.exec(rest);
    if (following) {
      to += following[0].length;
    } else if (/^(?:<\/p>|<br\s*\/?>|$)/i.test(rest)) {
      const before = html.slice(last, from);
      const preceding = /(?: |&nbsp;|\u00a0)$/.exec(before);
      if (preceding) from -= preceding[0].length;
    }
    out += html.slice(last, from);
    last = to;
  }
  return out + html.slice(last);
};

/**
 * What a resolved token says to assistive tech (#1599 Story 1.5, UX-DR3):
 * "mention, {Name}, you" for the viewer's own token, "mention, {Name}" for
 * anyone else's. Exported so the native span renderer (Story 3.2) speaks the
 * same words. Unescaped: the caller escapes for its medium.
 */
export const mentionLabel = (name: string, isViewer: boolean): string =>
  isViewer ? `mention, ${name}, you` : `mention, ${name}`;

/**
 * Every person token's text set from `message.mentions` (#1599 Story 1.4,
 * AC 10; correction 2: on web there is no per-element renderer, so this is a
 * string transform on the SANITIZED body, shared by both platforms). A token
 * whose id is in `mentions` keeps its span and reads `@${name}` with the
 * CURRENT name from the wire, so a renamed person reads right without a
 * rewrite; one whose id is not (a departed person, a self-mention, which
 * writes no row) is unwrapped to its stored text.
 *
 * #1599 Story 1.5 (correction 7): every RESOLVED token also gets `role="img"`
 * and an `aria-label` (`mentionLabel`), because `aria-label` on a role-less
 * `<span>` is prohibited by ARIA 1.2 and ignored by screen readers; and the
 * token naming `viewerId` gets `data-mention-viewer="true"`, which
 * `mentionTokenCss` tints. These attributes are POST-SANITIZE OUTPUT, never
 * input: the sanitizer's allow-list keeps only the id on a span, so a body
 * cannot carry its own (the crafted-body pin), and nothing widens an
 * allow-list. An unresolved token stays plain text with no label.
 *
 * #1599 Story 1.7 (correction 3): the group token is rewritten AFTER the
 * person pass, from the client constant (`CONFIG_TEAM_LABEL`), whatever its
 * stored text says and whether or not `mentions` arrived (the fan-out rows
 * never reach the wire, AD-4, so nothing on the message could name it). It
 * gets the same `role="img"` and label as a person token, and
 * `data-mention-viewer` when `viewerIsConfigTeam`: the viewer holds
 * Configuration Engineer, so the team token is their own.
 */
export const resolveMentionTokens = (
  html: string,
  mentions: readonly MessageMention[] | undefined,
  viewerId?: string,
  viewerIsConfigTeam = false,
): string =>
  resolveGroupTokens(
    resolvePersonTokens(html, mentions, viewerId),
    viewerIsConfigTeam,
  );

const resolveGroupTokens = (
  html: string,
  viewerIsConfigTeam: boolean,
): string =>
  html.replace(
    GROUP_TOKEN,
    (full: string, before: string, quote: string, after: string) => {
      // One span, one target: a span that is also a person token is the
      // person's (the server never stores both; the client sanitizer would).
      if (/\sdata-mention-user-id=/i.test(`${before} ${after}`)) return full;
      const viewer = viewerIsConfigTeam ? ' data-mention-viewer="true"' : '';
      return `<span${before} data-mention-group=${quote}config-team${quote}${after}${viewer} role="img" aria-label="${escapeHtml(
        mentionLabel(CONFIG_TEAM_LABEL, viewerIsConfigTeam),
      )}">@${escapeHtml(CONFIG_TEAM_LABEL)}</span>`;
    },
  );

const resolvePersonTokens = (
  html: string,
  mentions: readonly MessageMention[] | undefined,
  viewerId?: string,
): string => {
  // No `mentions` at all (a message cached before the field existed) is not
  // "nobody resolves": keep the stored tokens as they are.
  if (mentions === undefined) return html;
  const names = new Map<string, string>();
  for (const mention of mentions) names.set(mention.userId, mention.name);
  return html.replace(
    MENTION_TOKEN,
    (
      _full: string,
      before: string,
      quote: string,
      id: string,
      after: string,
      text: string,
    ) => {
      const name = names.get(id);
      if (name === undefined) return text;
      const isViewer = viewerId !== undefined && id === viewerId;
      const viewer = isViewer ? ' data-mention-viewer="true"' : '';
      return `<span${before} data-mention-user-id=${quote}${id}${quote}${after}${viewer} role="img" aria-label="${escapeHtml(
        mentionLabel(name, isViewer),
      )}">@${escapeHtml(name)}</span>`;
    },
  );
};

export const fileImageReference = (fileId: string): string =>
  `${FILE_IMAGE_SCHEME}://${fileId}`;

/** Every `<img>` whose src is a held-file ref: prefix, quote, id, suffix. */
const FILE_IMG_TAG =
  /<img\b([^>]*\bsrc=)(["'])correspondence-file:\/\/(\d+)\2([^>]*)>/gi;

/** Any `<img>` tag, and a held-file `src` attribute inside one. */
const ANY_IMG_TAG = /<img\b[^>]*>/gi;
const FILE_IMG_SRC = /\bsrc=(["'])correspondence-file:\/\/(\d+)\1/i;

/**
 * The held files the body shows INLINE, by id (owner, 2026-10-01 walk): a
 * picture already in the body is not repeated as a file row under it. It
 * stays in the Files strip, the zip and the viewer, which read the message's
 * full `attachments`; only the bubble's rows subtract it.
 */
export const inlineFileIds = (body: string): Set<string> =>
  new Set(Array.from(body.matchAll(FILE_IMG_TAG), m => m[3]));

/** Every staged-inline ref in the HTML, by staged id. */
const INLINE_REF = /correspondence-inline:\/\/([\w-]+)/g;

/**
 * The entities the server's `decodeTextEntities` decodes, in ITS order
 * (`&amp;` last, so a stored `&amp;lt;` is not decoded twice). The house
 * `convertToPlainText` already decodes four of these; this adds the two
 * quote forms so the client's count and the server's agree.
 */
const decodeRemainingEntities = (text: string): string =>
  text.replace(/&quot;/g, '"').replace(/&#39;|&#x27;/g, "'");

/**
 * The message's length IN TEXT, the way the server counts it
 * (`messageTextLength` in `correspondenceSanitizer.ts`): plain text of the
 * HTML with entities decoded, an `<img>` counted as its `[image]` label. That
 * last part is what makes a lone picture a non-empty message on both sides.
 */
export const messageTextLength = (html: string): number =>
  decodeRemainingEntities(convertToPlainText(html)).length;

export const isMessageTooLong = (html: string): boolean =>
  messageTextLength(html) > MAX_MESSAGE_LENGTH;

/** The server's `isMessageHtmlTooLarge`, mirrored: markup, not text. */
export const isMessageHtmlTooLarge = (html: string): boolean =>
  html.length > MAX_MESSAGE_HTML_LENGTH;

/** The server's refusal sentence for the markup ceiling, verbatim. */
export const MESSAGE_TOO_MUCH_FORMATTING =
  'Message contains too much formatting';

/**
 * A body stored before the editor (`5 &lt; 10\nline2`, no tags) as HTML:
 * entities decoded, then escaped again, so what the sanitizer escaped renders
 * as the characters typed, and newlines become `<br>`. `toRenderableRichHtml`
 * is NOT reused because a request field's legacy value is raw text while a
 * message's is already escaped: decoding first and then treating the result
 * as HTML would turn a typed `&lt;b&gt;` into markup.
 */
export const legacyMessageHtml = (body: string): string => {
  const decoded = decodeRemainingEntities(
    body
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&'),
  );
  return `<p>${escapeHtml(decoded).replace(/\r?\n/g, '<br>')}</p>`;
};

/** Message HTML as the sanitizer admits it, legacy bodies converted first. */
export const sanitizedMessageHtml = (body: string): string => {
  if (!body.trim()) return '';
  if (isLegacyPlainTextAreaValue(body)) return legacyMessageHtml(body);
  return sanitizeTextArea(body, {
    imgSchemes: [FILE_IMAGE_SCHEME],
    omitTags: MESSAGE_DROPPED_TAGS,
    omitStyles: MESSAGE_DROPPED_STYLES,
    extraAttributes: { span: MENTION_SPAN_ATTRIBUTES },
  });
};

const thumbnailById = (
  attachments: readonly MessageFile[] | undefined,
): Map<string, string> => {
  const byId = new Map<string, string>();
  for (const file of attachments ?? []) {
    if (file.kind === 'image' && file.thumbnailUri) {
      byId.set(file.id, file.thumbnailUri);
    }
  }
  return byId;
};

const imageFileIds = (
  attachments: readonly MessageFile[] | undefined,
): Set<string> =>
  new Set((attachments ?? []).filter(f => f.kind === 'image').map(f => f.id));

/** The bubble's HTML, and the held image each surviving `<img>` shows, in order. */
export interface MessageDisplay {
  html: string;
  /**
   * The file id behind the n-th `<img>` of `html`, document order: what an
   * image tap's ORDINAL resolves through (`pictureIndexForOrdinal`). A
   * placeholder is a `<span>`, so it has no entry here and no ordinal.
   */
  imageIds: readonly string[];
}

/**
 * The bubble's HTML: sanitized, every `correspondence-file://<id>` replaced
 * with that file's `thumbnailUri`, and an `<img>` whose ref matches no image
 * file of THIS message dropped. The server already guarantees the stored
 * body refers only to its own live image files; dropping here is defence in
 * depth, not redundancy (spec).
 *
 * A held image WITHOUT a thumbnail yet (the thumbnail fetch is a second call
 * and can lag or fail) is this message's picture all the same, so it is
 * shown as a visible placeholder in the house shape (`[image: alt]`) rather
 * than vanishing, which would read as the sender having sent nothing.
 */
export const messageDisplay = (
  body: string,
  attachments: readonly MessageFile[] | undefined,
  mentions: readonly MessageMention[] | undefined = [],
  /** #1599 Story 1.5: whose token is "you" (tinted and labelled so). */
  viewerId?: string,
  /**
   * #1599 Story 1.7: the viewer holds Configuration Engineer, so the team
   * token is "you" too.
   */
  viewerIsConfigTeam = false,
): MessageDisplay => {
  // #1599 Story 1.4: token text from the wire, AFTER the sanitizer (which
  // decides which tokens exist) and before the image rewrite.
  const html = resolveMentionTokens(
    sanitizedMessageHtml(body),
    mentions,
    viewerId,
    viewerIsConfigTeam,
  );
  if (!html) return { html: '', imageIds: [] };
  const thumbnails = thumbnailById(attachments);
  const held = imageFileIds(attachments);
  const imageIds: string[] = [];
  // Every `<img>`, not only the held-file ones: `sanitizeTextArea` always
  // admits the house `sitescope-attachment://` scheme, which a MESSAGE must
  // never show (request-scoped; it would leak an internal message's picture
  // to the customer), so anything that is not one of THIS message's
  // images is dropped whole.
  const rendered = html.replace(ANY_IMG_TAG, tag => {
    const ref = FILE_IMG_SRC.exec(tag);
    if (!ref || !held.has(ref[2])) return '';
    const thumbnail = thumbnails.get(ref[2]);
    if (!thumbnail) return imagePlaceholderForTag(tag);
    imageIds.push(ref[2]);
    return tag.replace(FILE_IMG_SRC, `src="${thumbnail}"`);
  });
  return { html: rendered, imageIds };
};

export const messageHtmlForDisplay = (
  body: string,
  attachments: readonly MessageFile[] | undefined,
  mentions: readonly MessageMention[] | undefined = [],
  viewerId?: string,
  viewerIsConfigTeam = false,
): string =>
  messageDisplay(body, attachments, mentions, viewerId, viewerIsConfigTeam)
    .html;

/**
 * Which picture the n-th `<img>` of the displayed body is, as an index into
 * the SAME list the bubble's file rows and the viewer use
 * (`partitionForDisplay(...).pictures`), or `-1` for an ordinal the body does
 * not have. BY ORDINAL, NEVER BY SRC: two held files can carry identical
 * thumbnail bytes, and a src lookup would open the first for both.
 */
export const pictureIndexForOrdinal = (
  attachments: readonly MessageFile[] | undefined,
  imageIds: readonly string[],
  ordinal: number,
): number => {
  const id = imageIds[ordinal];
  if (id === undefined) return -1;
  return partitionForDisplay(attachments ?? []).pictures.findIndex(
    f => f.id === id,
  );
};

/**
 * Put every held image's thumbnail in the preview map under its ref, so the
 * editors can SHOW `correspondence-file://<id>`: the web image node view
 * paints `pendingInlineImagePreviewSrc(src)`, and the native persist pass maps
 * a preview src back to the ref it was registered under.
 */
export const registerMessageImagePreviews = (
  attachments: readonly MessageFile[] | undefined,
): void => {
  for (const [id, thumbnail] of thumbnailById(attachments)) {
    rememberPendingInlineImagePreview(fileImageReference(id), thumbnail);
  }
};

/**
 * Held-file refs swapped for their preview srcs, for the NATIVE editor, which
 * shows a data URI directly and maps it back on persist. The message-side
 * twin of `rewriteHtmlAttachmentRefsToPreviewSrcs`, which is deliberately
 * `sitescope-attachment`-only (request code is not widened for messages).
 */
export const messageHtmlWithPreviewSrcs = (html: string): string =>
  html.replace(
    FILE_IMG_TAG,
    (full, prefix: string, quote: string, id: string, suffix: string) => {
      const preview = pendingInlineImagePreviewSrc(fileImageReference(id));
      return preview !== fileImageReference(id) &&
        isDisplayableInlineImageSrc(preview)
        ? `<img${prefix}${quote}${preview}${quote}${suffix}>`
        : full;
    },
  );

/**
 * What the composer is prefilled with to EDIT a stored body: the stored HTML
 * itself (never a text projection, Story 1.3's lesson), a legacy body
 * converted, previews registered. Web keeps the refs as `src` (Tiptap
 * rejects `data:` srcs and the node view paints from the preview map);
 * native swaps them for the data URI and its persist pass maps them back.
 * `platform` is a parameter so both branches are testable; callers leave it.
 */
export const messageHtmlForEditor = (
  body: string,
  attachments: readonly MessageFile[] | undefined,
  platform: string = Platform.OS,
): string => {
  registerMessageImagePreviews(attachments);
  const html = isLegacyPlainTextAreaValue(body)
    ? legacyMessageHtml(body)
    : body;
  return platform === 'web' ? html : messageHtmlWithPreviewSrcs(html);
};

/** The staged ids the HTML refers to as `correspondence-inline://<id>`. */
export const referencedInlineIds = (html: string): Set<string> => {
  const ids = new Set<string>();
  for (const match of html.matchAll(INLINE_REF)) {
    ids.add(match[1]);
  }
  return ids;
};

/**
 * The staged files after an HTML change: an inline file the HTML refers to is
 * marked `seen`; one that was seen and is no longer referred to is PRUNED
 * (its bytes are never counted or sent again); one never seen is kept, because
 * the native bridge can report a change from before the insert landed. Strip
 * files are untouched. Returns the same array when nothing changed, so the
 * caller can skip a write.
 */
export const reconcileInlineStaged = (
  html: string,
  staged: readonly StagedFile[],
): readonly StagedFile[] => {
  const referenced = referencedInlineIds(html);
  let changed = false;
  const next: StagedFile[] = [];
  for (const file of staged) {
    if (!file.inline) {
      next.push(file);
      continue;
    }
    if (referenced.has(file.id)) {
      if (file.seen) next.push(file);
      else {
        next.push({ ...file, seen: true });
        changed = true;
      }
      continue;
    }
    if (file.seen) {
      changed = true;
      continue;
    }
    next.push(file);
  }
  return changed ? next : staged;
};

/**
 * The body as it leaves the device: the house sanitizer with the message
 * rule's drops applied (so what the server will not keep is never sent) and
 * both message-image schemes admitted. Runs AFTER the ref rewrite, because
 * the sanitizer admits a scheme only in `<scheme>://<digits>` form and a
 * staged id is not digits. Anything else the sanitizer drops here (a
 * `data:` src the preview map missed, a foreign scheme) is what the server
 * would have dropped; dropping it a hop earlier is the same message.
 */
const outgoingMessageHtml = (html: string): string =>
  sanitizeTextArea(html, {
    imgSchemes: [INLINE_IMAGE_SCHEME, FILE_IMAGE_SCHEME],
    omitTags: MESSAGE_DROPPED_TAGS,
    omitStyles: MESSAGE_DROPPED_STYLES,
    extraAttributes: { span: MENTION_SPAN_ATTRIBUTES },
  });

/**
 * What goes on the wire (#1530): the strip files first, then every inline
 * file the HTML still refers to, in order of first reference, with each
 * `correspondence-inline://<stagedId>` rewritten to the file's INDEX in that
 * list. An inline file the HTML no longer refers to is neither sent nor
 * referenced. `files` are the `StagedFile`s that went, so the caller can
 * settle them by identity afterwards. The rewritten body then goes through
 * `outgoingMessageHtml`, so the wire carries the message rule's shape
 * (no `sub`/`sup`, no `text-align`/`font-family`) before the POST.
 */
export const outgoingMessage = (
  html: string,
  staged: readonly StagedFile[],
): { html: string; files: StagedFile[] } => {
  const strip = staged.filter(f => !f.inline);
  const inlineById = new Map(
    staged.filter(f => f.inline).map(f => [f.id, f] as const),
  );
  const files: StagedFile[] = [...strip];
  const indexById = new Map<string, number>();
  const rewritten = html.replace(
    INLINE_REF,
    (full: string, stagedId: string) => {
      const known = indexById.get(stagedId);
      if (known !== undefined) {
        return `${INLINE_IMAGE_SCHEME}://${known}`;
      }
      const file = inlineById.get(stagedId);
      if (!file) return full;
      const index = files.length;
      files.push(file);
      indexById.set(stagedId, index);
      return `${INLINE_IMAGE_SCHEME}://${index}`;
    },
  );
  return { html: outgoingMessageHtml(rewritten), files };
};

/** The wire shape of the files `outgoingMessage` chose. */
export const toOutgoingFiles = (
  files: readonly StagedFile[],
): OutgoingMessageFile[] =>
  files.map(f => ({
    fileName: f.name,
    mimeType: f.mimeType,
    fileData: f.base64,
  }));

export { inlineImageReference };
